import { useState, useEffect, createContext, useContext } from 'react';
import { authHelpers } from '../lib/supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session + merge server fields (occupation) for legacy localStorage
    const getInitialSession = async () => {
      const { session } = await authHelpers.getSession();
      let u = session?.user ?? null;
      if (u?.email && (u.occupation == null || u.occupation === '')) {
        const { data: row } = await authHelpers.fetchRegisteredUser();
        if (row) {
          u = {
            ...u,
            occupation: row.occupation != null && String(row.occupation).trim() ? row.occupation : null,
            name: row.name || u.name,
            phone: row.phone != null ? row.phone : u.phone,
          };
          try {
            const raw = JSON.parse(localStorage.getItem('kundli-session') || '{}');
            raw.user = { ...raw.user, ...u };
            localStorage.setItem('kundli-session', JSON.stringify(raw));
          } catch (_) {
            /* ignore */
          }
        }
      }
      setSession(session ? { ...session, user: u } : null);
      setUser(u);
      setLoading(false);
    };

    getInitialSession();

    // Check session every 5 minutes instead of 30 seconds
    const sessionCheckInterval = setInterval(() => {
      if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Checking session validity...');
      }
      getInitialSession();
    }, 300000); // 5 minutes

    return () => clearInterval(sessionCheckInterval);
  }, []);

  // Custom sign out that updates state
  const customSignOut = async () => {
    await authHelpers.signOut();
    setUser(null);
    setSession(null);
  };

  const handlePasswordAuth = (sessionData) => {
    if (sessionData?.user) {
      setUser(sessionData.user);
      setSession(sessionData);
    }
  };

  const customSignUp = async (signupPayload) => {
    const result = await authHelpers.signUpWithPassword(signupPayload);
    if (result.data) handlePasswordAuth(result.data);
    return result;
  };

  const customSignIn = async (email, password) => {
    const result = await authHelpers.signInWithPassword(email, password);
    if (result.data) handlePasswordAuth(result.data);
    return result;
  };

  const setOccupationOnUser = async (occupation) => {
    const { data, error } = await authHelpers.updateRegisteredOccupation(user?.email, occupation);
    if (error || !data) return { error: error || { message: 'Failed' } };
    const merged = { ...user, occupation: data.occupation != null ? data.occupation : null };
    setUser(merged);
    try {
      const raw = JSON.parse(localStorage.getItem('kundli-session') || '{}');
      raw.user = { ...raw.user, ...merged };
      localStorage.setItem('kundli-session', JSON.stringify(raw));
    } catch (_) {
      /* ignore */
    }
    setSession((prev) => (prev?.user ? { ...prev, user: merged } : prev));
    return { data: merged, error: null };
  };

  const value = {
    user,
    session,
    loading,
    signOut: customSignOut,
    signUp: customSignUp,
    signIn: customSignIn,
    setOccupationOnUser,
    requestPasswordReset: authHelpers.requestPasswordReset,
    verifyPasswordResetOtp: authHelpers.verifyPasswordResetOtp,
    resetPasswordWithOtp: authHelpers.resetPasswordWithOtp,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const useSession = () => {
  const { session } = useAuth();
  return session;
};
